export type WorkflowActionType =
  | 'send_email'
  | 'send_slack'
  | 'call_webhook'
  | 'notify_teams'
  | 'if_else';

export type WorkflowBranch = 'then' | 'else';

export interface WorkflowActionCatalogItem {
  id: WorkflowActionType;
  label: string;
  description: string;
  defaultConfig: Record<string, string>;
}

export interface WorkflowPosition {
  x: number;
  y: number;
}

export interface WorkflowActionParent {
  parentId: string | null;
  branch: WorkflowBranch | null;
}

export interface WorkflowAction {
  id: string;
  type: WorkflowActionType;
  label: string;
  config: Record<string, string>;
  x: number;
  y: number;
  parentId: string | null;
  branch: WorkflowBranch | null;
}

export const WORKFLOW_ACTION_CATALOG: WorkflowActionCatalogItem[] = [
  {
    id: 'send_email',
    label: 'Send email',
    description: 'Email a recipient when the form is submitted.',
    defaultConfig: {
      to: '',
      subject: 'New form submission',
      body: 'You received a new response.',
    },
  },
  {
    id: 'send_slack',
    label: 'Send Slack message',
    description: 'Post a message to a Slack channel.',
    defaultConfig: {
      channel: '#submissions',
      message: 'New form submission received.',
    },
  },
  {
    id: 'call_webhook',
    label: 'Call webhook',
    description: 'Send the submission payload to an HTTP endpoint.',
    defaultConfig: {
      url: '',
      method: 'POST',
    },
  },
  {
    id: 'notify_teams',
    label: 'Notify Microsoft Teams',
    description: 'Send a notification to a Teams channel webhook.',
    defaultConfig: {
      webhookUrl: '',
      message: 'New form submission received.',
    },
  },
  {
    id: 'if_else',
    label: 'If / else',
    description: 'Branch the workflow on a condition.',
    defaultConfig: {
      condition: '',
    },
  },
];

let actionCounter = 0;

export function createActionFromCatalog(
  item: WorkflowActionCatalogItem,
  position: WorkflowPosition = { x: 0, y: 0 },
  parent: WorkflowActionParent = { parentId: null, branch: null },
): WorkflowAction {
  actionCounter += 1;
  return {
    id: `workflow-action-${Date.now()}-${actionCounter}`,
    type: item.id,
    label: item.label,
    config: { ...item.defaultConfig },
    x: position.x,
    y: position.y,
    parentId: parent.parentId,
    branch: parent.branch,
  };
}

export function rootActions(actions: WorkflowAction[]): WorkflowAction[] {
  return actions.filter((action) => action.parentId == null);
}

export function branchActions(
  actions: WorkflowAction[],
  parentId: string,
  branch: WorkflowBranch,
): WorkflowAction[] {
  return actions.filter(
    (action) => action.parentId === parentId && action.branch === branch,
  );
}

export function collectDescendantIds(
  actions: WorkflowAction[],
  rootId: string,
): Set<string> {
  const ids = new Set<string>([rootId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const action of actions) {
      if (action.parentId && ids.has(action.parentId) && !ids.has(action.id)) {
        ids.add(action.id);
        grew = true;
      }
    }
  }
  return ids;
}

export function summarizeWorkflowAction(action: WorkflowAction): string {
  switch (action.type) {
    case 'send_email':
      return action.config['to']?.trim()
        ? `To ${action.config['to']}`
        : 'Recipient not set';
    case 'send_slack':
      return action.config['channel']?.trim()
        ? `Channel ${action.config['channel']}`
        : 'Channel not set';
    case 'call_webhook':
      return action.config['url']?.trim()
        ? `${action.config['method'] || 'POST'} ${action.config['url']}`
        : 'URL not set';
    case 'notify_teams':
      return action.config['webhookUrl']?.trim()
        ? 'Teams webhook configured'
        : 'Webhook URL not set';
    case 'if_else':
      return action.config['condition']?.trim()
        ? action.config['condition']
        : 'Condition not set';
    default:
      return 'Not configured';
  }
}
